import {
  computed,
  type InjectionKey,
  provide,
  inject,
  type Ref,
  onMounted,
  onUnmounted,
} from 'vue';

import { useLocalStorage, useMediaQuery } from '@vueuse/core';

export interface SidebarContext {
  readonly isExpanded: Ref<boolean>;
  readonly isPinned: Ref<boolean>;
  readonly isMobile: Ref<boolean>;
  readonly isCollapsed: Ref<boolean>;
  readonly contentMargin: Ref<string>;
  toggle(): void;
  close(): void;
  togglePin(): void;
}

const SIDEBAR_KEY: InjectionKey<SidebarContext> = Symbol('sidebar');

export function provideSidebar(): SidebarContext {
  const isExpanded = useLocalStorage('sidebar-expanded', false);
  const isPinned = useLocalStorage('sidebar-pinned', false);
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const isCollapsed = computed(() => !isExpanded.value && !isMobile.value);

  const contentMargin = computed(() => {
    if (isMobile.value) {
      return '0';
    }

    if (isExpanded.value && isPinned.value) {
      return '16rem';
    }

    return '4.5rem';
  });

  function toggle(): void {
    isExpanded.value = !isExpanded.value;
  }

  function close(): void {
    isExpanded.value = false;
  }

  function togglePin(): void {
    isPinned.value = !isPinned.value;
  }

  function handleKeydown(event: KeyboardEvent): void {
    const modifier = event.metaKey || event.ctrlKey;

    if (modifier && event.key === 'b') {
      event.preventDefault();
      toggle();

      return;
    }

    if (event.key === 'Escape' && isExpanded.value) {
      event.preventDefault();
      close();
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });

  const context: SidebarContext = {
    isExpanded,
    isPinned,
    isMobile,
    isCollapsed,
    contentMargin,
    toggle,
    close,
    togglePin,
  };

  provide(SIDEBAR_KEY, context);

  return context;
}

export function useSidebar(): SidebarContext {
  const context = inject(SIDEBAR_KEY);

  if (!context) {
    throw new Error('useSidebar() must be used within a component that calls provideSidebar()');
  }

  return context;
}
